import React, { useCallback, useEffect } from "react";
import { observer } from "mobx-react-lite";
import { useTranslation } from "react-i18next";
import BaseIcon from "../../../../components/BaseIcon";
import PointsBurst from "../../../../components/PointsBurst";
import Points from "../../../../store/Points";
import "./style.scss";

// Баллы вверху ЛК + шкала прогресса по порогам покупок за баллы (тарифы 03.08.2026):
// 500 (пустая анкета) · 900 (тест-88, пазл-контур) · 1900 (+57, пазл-заливка) ·
// 4900 (игра, бабочка). Четыре тика. % — позиции по центрам иконок вдоль трека;
// линия тянется левее первого и правее последнего тика. Заливка (см. ниже) идёт
// непрерывно по значению баланса. Иконка теста-88 — контур (pts_puzzle_outline),
// +57 — заливка (pts_puzzle), чтобы визуально различать наборы карт.
const MILESTONES = [
  { value: 500, icon: "pts_anketa", left: 13 },
  { value: 900, icon: "pts_puzzle_outline", left: 30 },
  { value: 1900, icon: "pts_puzzle", left: 58 },
  { value: 4900, icon: "butterfly_gradient", left: 87 },
];

export default observer(function PointsScale() {
  const { t } = useTranslation();

  useEffect(() => {
    if (Points.balance === null) Points.fetchBalance();
  }, []);

  const onBurstDone = useCallback(() => Points.consumeAccrual(), []);

  const balance = Points.balance ?? 0;
  // Заливка идёт НЕПРЕРЫВНО (кусочно-линейно) по значению баланса, а не скачками
  // до достигнутого деления — иначе, например, 700 «застревал» бы на метке 500.
  // Опорные точки: (0 → левый край) + MILESTONES (500·900·1900·4900). Между ними
  // ширина интерполируется, поэтому промежуточные балансы видны полосой между
  // делениями. Цифровые деления (MILESTONES) не меняются.
  const ANCHORS = [{ value: 0, left: 0 }, ...MILESTONES];
  const last = ANCHORS[ANCHORS.length - 1];
  let fillWidth;
  if (balance <= 0) {
    fillWidth = 0;
  } else if (balance >= last.value) {
    fillWidth = last.left;
  } else {
    const i = ANCHORS.findIndex((a) => balance < a.value);
    const lo = ANCHORS[i - 1];
    const hi = ANCHORS[i];
    const frac = (balance - lo.value) / (hi.value - lo.value);
    fillWidth = lo.left + frac * (hi.left - lo.left);
  }

  return (
    <div className="points-scale">
      <div className="points-scale__head">
        <BaseIcon name="star" className="points-scale__star" />
        <span className="points-scale__value">{balance}</span>
        <span className="points-scale__label">{t("loyalty.points_word")}</span>
        {Points.lastAccrual ? (
          <PointsBurst amount={Points.lastAccrual.amount} onDone={onBurstDone} />
        ) : null}
      </div>

      <div className="points-scale__track">
        <div className="points-scale__track-line" />
        <div
          className="points-scale__track-fill"
          style={{ width: `${fillWidth}%` }}
        />
        {MILESTONES.map((m) => {
          const reached = balance >= m.value;
          return (
            <div
              key={m.value}
              className={`points-scale__tick${
                reached ? " points-scale__tick--reached" : ""
              }`}
              style={{ left: `${m.left}%` }}
            >
              <BaseIcon name={m.icon} className="points-scale__tick-icon" />
              <span className="points-scale__tick-dot" />
              <span className="points-scale__tick-value">{m.value}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
});
